<template>
  <div>
    <Teleport to="#top-menu-bar" defer>
      <PageTopBar>
        <template #item-name>Import Workflow and Version</template>
      </PageTopBar>
    </Teleport>
    <div
      v-if="isMetaReady"
      :key="$route.fullPath"
      class="detail-container padded-container my-5"
    >
      <div class="detail-panels">
        <div class="box is-flex import-workflow">
          <div class="mr-5">
            <span class="icon is-large has-text-grey">
              <i v-if="!isLoadingWorkflow" class="fa-brands fa-git-alt fa-3x" />
              <i
                v-else
                class="fa-solid fa-spinner fa-spin fa-2x"
                style="opacity: 0.6"
              />
            </span>
          </div>
          <GitWorkflowImport
            :key="`git-import-${workflowId ? workflowId : 'new'}`"
            v-bind="$props"
            :icon-class="'fa-brands fa-git-alt'"
            :workflow-data="workflow"
            :workflow-name="workflowName"
            style="min-width: 0"
            @update-name="(n) => (workflowName = n)"
          >
            <template #workflowName="{ canChange }">
              <div class="field mt-2 mb-2">
                <label class="label form-label">
                  <span class="is-size-7 mr-2">Workflow Name</span>
                  <span class="icon is-small has-text-danger">
                    <i class="fa-solid fa-asterisk fa-xs" />
                  </span>
                </label>
                <div class="field is-expanded m-0">
                  <div class="control is-expanded">
                    <input
                      v-model="workflowName"
                      class="input"
                      :class="{ 'is-danger': v$.workflowName.$invalid }"
                      type="text"
                      placeholder="Workflow Name"
                      :disabled="!canChange"
                    />
                    <div
                      v-if="v$.workflowName.$errors.length > 0"
                      class="help is-danger is-inline"
                    >
                      {{ v$.workflowName.$errors[0].$message }}
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </GitWorkflowImport>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { usePageShell } from '@/composables/usePageShell'
import { useRecord } from '@/composables/useRecord'
import { useRecordMeta } from '@/composables/useRecordMeta'
import { useStore } from '@/store'
import { isUUID } from '@/utils'

import GitWorkflowImport from '@/components/GitWorkflowImport.vue'
import PageTopBar from '@/components/PageTopBar.vue'

export default {
  name: 'WorkflowImportView',
  components: {
    GitWorkflowImport,
    PageTopBar,
  },
  props: {
    appName: { type: String },
    modelName: { type: String },
    modelType: { type: String },
  },
  setup(props) {
    const route = useRoute()
    const store = useStore()

    const qWorkflowId = computed(() => route?.query?.workflowId)
    let workflow = ref()
    let workflowName = ref()
    let workflowId = ref()
    let isLoadingWorkflow = ref()

    function isValidUuid(id) {
      let isValid = true
      if (!isUUID(id)) {
        isValid = false
        store.commit('notify', {
          type: 'error',
          message: 'Found Invalid workflow ID in URL',
          isDismissable: true,
        })
      }
      return isValid
    }
    watch(
      qWorkflowId,
      (newId, oldID) => {
        if (newId && newId !== oldID && isValidUuid(newId)) {
          // updateUrl()
          const recordResult = useRecord(
            {
              appName: props.appName,
              modelName: props.modelName,
              id: newId,
            },
            { queryParameters: { format: 'json', response_format: 'flat' } },
          )
          workflow.value = recordResult.record.value
          watch(
            recordResult.name,
            (name) => {
              workflowName.value = name
            },
            { immediate: true },
          )

          watch(
            recordResult.id,
            (id) => {
              workflowId.value = id
            },
            { immediate: true },
          )
          watch(
            recordResult.isLoadingRecord,
            (v) => (isLoadingWorkflow.value = v),
            {
              immediate: true,
            },
          )
        }
      },
      {
        immediate: true,
      },
    )

    const v$ = useVuelidate(
      { workflowName: { required } },
      { workflowName },
      { $autoDirty: true },
    )

    const { isMetaReady, modelAttributes, viewAttributes, orderedFields } =
      useRecordMeta({
        appName: props.appName,
        modelName: props.modelName,
        modelType: props.modelType,
      })

    usePageShell({ title: 'Import' })

    return {
      isMetaReady,
      modelAttributes,
      viewAttributes,
      orderedFields,
      // Existing Workflow
      workflow,
      workflowId,
      workflowName,
      isLoadingWorkflow,
      v$,
      route,
    }
  },
}
</script>

<style lang="scss" scoped>
.is-inline {
  display: inline;
}
.is-inline-block {
  display: inline-block;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
